/* eslint-disable */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ArrowDown, ArrowRight, ArrowUp, ArrowUpDown, BarChart3, Filter, LucideBarChartBig, Workflow, WorkflowIcon } from 'lucide-react';
import GlobalSearch from '@/Components/globalSearch/search';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import { Button } from '@/Components/ui/button';
import { filtersInfoChip, formatDate, useQuery } from '@/Helpers';
import drawerActions from '@/Actions/drawer.actions';
import { parentTabs, subParentTabs } from './tabs.constants';
import { useAuth } from '@/Components/Auth/Login/auth';
import AmountContainerDiv from '@/Components/Forms/Payment Settlements/AmountContainerDiv';
import SortComp from './SortComp';
import TableWithTabs from './TableWithTabs';
import { useAiFmsData } from './hooks/useAiFmsData';
import { useTableState } from './hooks/useTableState';
import { useTableData } from './hooks/useTableData';
import { BackdropLoader } from '@/Components/Loaders';
import { getActiveTabForFetch, getActualProductPrice, getDropDowns, getSortOptions } from './helper';
import { urls } from '@/Constants';
import { modalActions } from '@/Actions';
import bowl from '@/Images/cookingbowl.svg';

const filtersInfo = {
  header: { store: false, date: false },
  kitchenBaseStore: { text: 'Kitchen ', show: true },
  pods: { text: 'Pods', show: false },
  dateRange: { text: 'Date Range', show: true },
  orderType: { text: 'Tab Type' },
};

const exportInfo = { showExportBtn: true, };

const LoadingSpinner = (isLoading) => (
  <BackdropLoader isLoading={isLoading} showProgress={isLoading} />
);

function AiFmsPage() {
  const params = useQuery();
  const dispatch = useDispatch();
  const { appConfig = {} } = useAuth();
  const isNagaCustomer = appConfig?.customerId === 72;
  const formStore = params.get('fromPod');
  const toPodFilter = params.get('toPod');
  const parentValue = params.get('parentValue');
  const mealType = params.get('mealType');
  const mealTypeId = params.get('mealTypeId');
  const rowIndex = params.get('rowIndex');
  const parentId = params.get('parentId');
  const subTab = params.get('subTab');
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState({});
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sort, setSort] = useState({});
  const [actualProductPrice, setActualProductPrice] = useState({});
  const [idealCost, setIdealCost] = useState({});
  const [isTabChanging, setIsTabChanging] = useState(false);

  // Filter tabs based on customer
  const filteredParentTabs = useMemo(() => {
    return parentTabs.filter(tab => {
      if (!tab.showCustomer) return true;
      if (tab.showCustomer === 'naga') return isNagaCustomer;
      if (tab.showCustomer === 'a2b') return !isNagaCustomer;
      return true;
    });
  }, [isNagaCustomer]);

  const {
    rawData,
    producedData,
    issuedData,
    statsSummary,
    isLoading,
    fetchData,
    fetchProducedData,
    fetchCogsAnalysisGraph,
    fetchIssuedData,
    handleExport,
  } = useAiFmsData({
    parentTab: parentTabs[0],
    formStore,
    toPodFilter
  });

  const {
    parentTab,
    active,
    selectedTab,
    tableTab,
    mealTabsList,
    data,
    setSelectedTab,
    handleParentTab,
    handleMealTab,
    getTabInfo,
    getAvailableTabs,
    subParentTab,
    setSubParentTab,
    handleSubParentTab,
  } = useTableState(rawData, setSort, setSearch, isNagaCustomer);

  const { getTableInfo } = useTableData({
    parentTab,
    selectedTab,
    tableTab,
    data,
    producedData,
    active,
    filters,
    search,
    sort,
    actualProductPrice,
    idealCost,
    subParentTab,
  });

  useEffect(() => {
    if ((mealType || mealTypeId) && mealTabsList.length > 0) {
      let cleanMealType = mealType;
      if (mealType) {
        cleanMealType = mealType.replace(/^"(.*)"$/, '$1');
      }
      if (mealTypeId) {
        cleanMealType = mealTypeId;
      }
      let foundMealTab;
      if (mealTypeId) {
        const idNum = Number(cleanMealType);
        foundMealTab = mealTabsList.find(meal => Number(meal.id) === idNum);
      } else {
        foundMealTab = mealTabsList.find(meal => meal?.shiftName?.toLowerCase() === cleanMealType.toLowerCase());
      }
      if (foundMealTab) {
        handleMealTab(foundMealTab);
      }
    }
  }, [mealType, mealTabsList, handleMealTab, mealTypeId]);


  const handleParentTabChange = useCallback(async (tab) => {
    handleParentTab(tab);
    setSearch({});
    const newSubParentTab = subParentTabs[tab?.id]?.[0];
    setSubParentTab(newSubParentTab);
    setSelectedTab('weight');
    setSort({});
    if (filters.dateRange) {
      const getTab = getActiveTabForFetch(tab, newSubParentTab);
      await fetchData(filters, getTab);
    }
  }, [handleParentTab, filters, fetchData, setSubParentTab]);

  const handleMealTabChange = useCallback((tab) => {
    setIsTabChanging(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        handleMealTab(tab);
        setSearch({});
        setSort({});
        requestAnimationFrame(() => {
          setIsTabChanging(false);
        });
      }, 0);
    });
  }, [handleMealTab]);

  const handleSetSelectedTab = useCallback((tabId) => {
    setSelectedTab(tabId);
    setSearch({});
    setSort({});
    const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : null;
    fetchCogsAnalysisGraph(filters.dateRange, active?.id, getSelectedTab);
  }, [setSelectedTab, filters.dateRange, active?.id, fetchCogsAnalysisGraph, selectedTab]);

  useEffect(() => {
    if (parentValue) {
      const cleanParentValue = parentValue.replace(/^"(.*)"$/, '$1');
      const foundParentTab = parentTabs.find(tab => tab.value === cleanParentValue);
      if (foundParentTab) {
        handleParentTab(foundParentTab);
      } else {
        const subTabs = Object?.values(subParentTabs)?.flat()?.find(tab => tab.value === cleanParentValue);
        if (subTabs) {
          handleSubParentTab(subTabs);
        }
      }
    } else {
      if (parentId) {
        const cleanSubTabValue = subTab.replace(/^"(.*)"$/, '$1');
        const foundParentTab = parentTabs.find(tab => tab.id === Number(parentId));
        if (foundParentTab) {
          const foundSubTab = subParentTabs[foundParentTab?.id]?.find(tab => tab.value === cleanSubTabValue);
          if (foundSubTab) {
            handleSubParentTab(foundSubTab);
          }
          handleParentTab(foundParentTab);
        }
      }
    }
  }, [parentValue, parentId, subTab, handleParentTabChange, handleSubParentTab]);

  const handleSortChange = useCallback((value, type, attr) => {
    setSort({
      sortColumnName: value,
      sortType: type,
      sortAttribute: attr,
    });
  }, []);

  const handleDataFetch = useCallback((filter) => {
    setFilters(filter);
    const getActiveTab = getActiveTabForFetch(parentTab, subParentTab);
    if (parentValue || parentId) {
      if (parentValue) {
        const cleanParentValue = parentValue.replace(/^"(.*)"$/, '$1');
        const foundParentTab = parentTabs.find(tab => tab.value === cleanParentValue);
        if (foundParentTab) {
          fetchData(filter, foundParentTab);
        } else {
          const subTabs = Object?.values(subParentTabs)?.flat()?.find(tab => tab.value === cleanParentValue);
          if (subTabs) {
            fetchData(filter, subTabs);
          }
        }
      } else {
        const foundParentTab = parentTabs.find(tab => tab.id === Number(parentId));
        const cleanSubTabValue = subTab.replace(/^"(.*)"$/, '$1');
        const foundSubTab = subParentTabs[foundParentTab?.id]?.find(tab => tab.value === cleanSubTabValue);
        fetchData(filter, foundSubTab);
      }
    } else {
      fetchData(filter, getActiveTab);
    }
  }, [parentTab?.id, subParentTab?.id]);

  useEffect(() => {
    if (tableTab && parentTab?.id === 2) {
      if (filters.dateRange) {
        const getTab = getActiveTabForFetch(parentTab, subParentTab);
        if (tableTab === 'sfg') {
          fetchData(filters, getTab, 'INTERMEDIATE');
        } else {
          fetchData(filters, getTab);
        }
      }
    }
  }, [tableTab, filters])

  useEffect(() => {
    if (parentTab?.id === 1) {
      fetchProducedData(filters);
    }
    fetchIssuedData(filters);
  }, [parentTab?.id, fetchProducedData, filters, fetchIssuedData]);

  useEffect(() => {
    if (filters?.dateRange && active?.id) {
      const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : null;
      fetchCogsAnalysisGraph(filters.dateRange, active.id, getSelectedTab);
    }
  }, [filters?.dateRange, active?.id, fetchCogsAnalysisGraph, selectedTab]);

  useEffect(() => {
    if (active?.id) {
      const issuedDataList = issuedData?.find((item) => item?.mealType === active?.value)?.items ?? {};
      const actualProductPrice = getActualProductPrice(issuedDataList);
      setActualProductPrice(actualProductPrice?.actualPrice);
      setIdealCost(actualProductPrice?.idealCost);
    }
  }, [active?.id, issuedData]);

  const handleSubParentTabChange = useCallback(async (tab) => {
    setSubParentTab(tab);
    setSearch({});
    setSort({});
    if (filters.dateRange) {
      await fetchData(filters, tab);
    }
  }, [setSubParentTab, filters, fetchData, parentTab, subParentTab]);


  const FilterSection = useMemo(() => (
    <div className='flex flex-1 items-baseline gap-3'>
      <div className='flex-1 min-w-0'>
        <GlobalSearch
          placeholderText='Search'
          dropDownOptions={getDropDowns(parentTab, subParentTab)}
          setSelectedValue={setSearch}
          searchDropDown={parentTab?.id !== 4}
        />
      </div>
      <div className='flex-shrink-0'>
        {filtersInfoChip(filtersInfo, filters)}
      </div>
      <Button
        onClick={() => dispatch(drawerActions.toggleFiltersDrawer(true, {
          handleDataExport: (filtersState) =>
            handleExport(filtersState, getActiveTabForFetch(parentTab, subParentTab)),
        }))}
        className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700 flex-shrink-0'
      >
        <span>Filter & Export</span>
        <Filter size={16} />
      </Button>
    </div>
  ), [filters, dispatch, parentTab, handleExport, getDropDowns, subParentTab]);

  const ParentTabsSection = useMemo(() => (
    <div className='flex justify-center gap-2 rounded-md bg-[#FDE6AD] border border-[#00000033]'>
      {filteredParentTabs.map((ele) => {
        const isActive = parentTab?.id === ele?.id;
        return (
          <div
            key={ele.id}
            className={`text-center px-4 !text-[14px] py-2 text-base font-medium cursor-pointer min-w-max
              ${isActive ? 'bg-[#ffb703]' : ''}`}
            onClick={() => handleParentTabChange(ele)}
          >
            {ele?.header}
          </div>
        );
      })}
    </div>
  ), [parentTab, handleParentTabChange, filteredParentTabs]);

  const MealTabsSection = useMemo(() => (
    <div className='inline-flex flex-wrap justify-center gap-2 m-[12px] rounded-md bg-[#FDE6AD] border border-[#00000033]'>
      {mealTabsList.map((ele) => {
        const isActive = active?.id === ele?.id;
        return (
          <div
            key={ele.id}
            className={`text-center !text-[14px] px-4 py-2 text-base font-medium cursor-pointer min-w-max
              ${isActive ? 'bg-[#ffb703]' : ''}`}
            onClick={() => handleMealTabChange(ele)}
          >
            {ele?.shiftName}
          </div>
        );
      })}
    </div>
  ), [mealTabsList, active, handleMealTab]);

  const TabsSection = useMemo(() => (
    <div className='w-fit inline-flex rounded-lg bg-[#FDE6AD] p-1 m-[12px]'>
      {getAvailableTabs().map((tab) => (
        <button
          key={tab.id}
          onClick={() => handleSetSelectedTab(tab.id)}
          className={`px-4 py-2 flex items-center text-sm font-medium transition-all duration-200 rounded-md ${selectedTab === tab.id
            ? 'bg-yellow-400 text-black shadow-sm'
            : 'text-gray-700 hover:bg-yellow-300/50'
            }`}
        >
          <span className='mr-2'>{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  ), [getAvailableTabs, selectedTab, setSelectedTab]);

  const subParentDiv = useMemo(() => {
    const filteredSubTabs = subParentTabs[parentTab?.id]?.filter(tab => {
      if (!tab.showCustomer) return true;
      if (tab.showCustomer === 'naga') return isNagaCustomer;
      if (tab.showCustomer === 'a2b') return !isNagaCustomer;
      return true;
    }) || [];

    return (
      <div className='w-fit inline-flex flex-wrap justify-center gap-2 m-[12px] rounded-md bg-[#FDE6AD] border border-[#00000033]'>
        {filteredSubTabs.map((ele) => {
          const isActive = subParentTab?.id === ele?.id;
          return (
            <div
              key={ele.id}
              className={`text-center !text-[14px] px-4 py-2 text-base font-medium cursor-pointer min-w-max
            ${isActive ? 'bg-[#ffb703]' : ''}`}
              onClick={() => handleSubParentTabChange(ele)}
            >
              {ele?.header}
            </div>
          );
        })}
      </div>
    );
  }, [parentTab, subParentTabs, subParentTab, handleSubParentTabChange, isNagaCustomer])


  const shouldHideSort = useMemo(() => {
    const isWeightOrPercentage = selectedTab === 'weight' || selectedTab === 'percentage';
    if (parentTab?.id === 1 && subParentTab?.id === 1 && tableTab === 'rawMaterials' && isWeightOrPercentage) return true;
    if (parentTab?.id === 2 && (tableTab === 'products' || tableTab === 'sfg') && isWeightOrPercentage) return true;
    if (parentTab?.id === 4) return true;

    return false;
  }, [parentTab?.id, tableTab, selectedTab, subParentTab?.id]);

  const shouldHideSortType = useMemo(() => {
    const isCost = selectedTab === 'cost';
    if (parentTab?.id === 1 && subParentTab?.id === 1 && tableTab === 'rawMaterials' && isCost) return true;
    if (parentTab?.id === 2 && (tableTab === 'products' || tableTab === 'sfg') && isCost) return true;

    return false;
  }, [parentTab?.id, tableTab, selectedTab, subParentTab?.id]);

  const getSortType = (columnName, sortType) => {
    if (columnName) {
      const name = getSortOptions(parentTab, selectedTab, tableTab, subParentTab).find(option => option.value === columnName)?.label;
      const sortTypeIcon = sortType === 'ascending' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
      return <span className='flex items-start gap-1'> <strong className='flex items-center gap-1'> <ArrowUpDown size={16} /> Sort By:</strong> {name} {sortTypeIcon}</span>;
    }
    return <span className='flex items-start gap-1'> <ArrowUpDown size={16} /> Sort By</span>;
  };

  const getDefaultSort = useMemo(() => {
    if (parentTab?.id === 1 && subParentTab?.id === 1 && selectedTab === 'cost' && tableTab === 'rawMaterials') return 'costPrice';
    if (parentTab?.id === 2 && selectedTab === 'cost' && (tableTab === 'products' || tableTab === 'sfg')) return 'costPrice';
    return '';
  }, [selectedTab, tableTab, subParentTab, parentTab]);

  const SortSection = useMemo(() => (
    <div className='relative'>
      <button
        onClick={() => setIsSortOpen(true)}
        className='px-4 py-2 flex items-center text-sm font-medium transition-all duration-200 rounded-md bg-yellow-400 text-black shadow-sm'
      >
        {getSortType(sort?.sortColumnName, sort?.sortType)}
      </button>
      <SortComp
        isOpen={isSortOpen}
        setIsOpen={setIsSortOpen}
        onSortChange={handleSortChange}
        sortOptions={getSortOptions(parentTab, selectedTab, tableTab, subParentTab)}
        hideSortType={shouldHideSortType}
        defaultSort={getDefaultSort}
      />
    </div>
  ), [isSortOpen, handleSortChange, getSortOptions, shouldHideSortType, sort]);

  const getIndex = useCallback((rowIndex) => {
    if (parentValue) {
      const cleanParentValue = parentValue.replace(/^"(.*)"$/, '$1');
      const foundParentTab = parentTabs.find(tab => tab.value === cleanParentValue);

      if (foundParentTab) {
        if (foundParentTab?.id === parentTab?.id) {
          if (
            (parentTab?.id === 2 && tableTab === 'outlets') ||
            parentTab?.id === 3
          ) {
            return rowIndex;
          }
        }
      }
    } else {
      if (parentId) {
        const foundParentTab = parentTabs.find(tab => tab.id === Number(parentId));
        const cleanSubTabValue = subTab.replace(/^"(.*)"$/, '$1');
        const foundSubTab = subParentTabs[foundParentTab?.id]?.find(tab => tab.value === cleanSubTabValue);
        if (foundSubTab) {
          if (
            (parentTab?.id === 1 && subParentTab?.id === 1 && tableTab === 'products') ||
            (parentTab?.id === 1 && subParentTab?.id === 2 && tableTab === 'section')
          ) {
            return rowIndex;
          }
        }
      }
    }
    return null;
  }, [parentValue, tableTab, parentTab, parentTabs, subParentTab]);

  const renderLegend = () => {
    return (
      <div className='flex items-center gap-3 text-xs'>
        <span>Some Days don’t have any production measured through UY system, please add the data manually for accurate costing.</span>
        <Button
          onClick={() => dispatch(modalActions.toggleMultiplePoModal(true, {
            mode: 'dataEntry',
          }))}

          className='bg-yellow-400 hover:bg-yellow-500 rounded-md flex items-center justify-center gap-2 text-black'
        >
          <img src={bowl} alt="" />
          Add Manually
        </Button>
      </div>
    )
  }

  return (
    <div className='p-2'>
      <ImagesFinder
        filtersInfo={filtersInfo}
        exportInfo={exportInfo}
        onSubmit={handleDataFetch}
        disableFutureDate
        hideDateRangeTimeType
      />

      {(isLoading || isTabChanging) ? (
        <LoadingSpinner isLoading={isLoading || isTabChanging} />
      ) : (
        <>
          <div className='flex flex-col gap-2'>
            <div className='flex justify-between items-center'>
              <h2 className='font-bold text-lg'>AI FMS Summary 2.0</h2>
              <Button
                className="underline-animation w-fit bg-yellow-50 text-yellow-600 hover:bg-yellow-100 font-semibold text-[12px] transition-colors duration-200"
                onClick={() => window.open(`${urls.EXTENSION}${urls.PROCESS_FLOW_NEW}?&store=${filters?.fromPodFilter?.id ? JSON.stringify(filters?.fromPodFilter) : null}&dateOnly=${encodeURIComponent(JSON.stringify({ date: filters?.dateRange?.to }))}&shiftId=${active?.id}`, '_blank')}
              >
                <WorkflowIcon size={20} />
                <span>View Operation Flow</span>
                <ArrowRight size={18} />
              </Button>
            </div>
            {FilterSection}
            <div className='flex justify-between items-center'>
              {ParentTabsSection}
              {TabsSection}
              {MealTabsSection}
            </div>
            {parentTab?.id === 1 && subParentDiv}
            <AmountContainerDiv amountCaption={statsSummary} />

            <div className='flex items-center gap-3 justify-end'>
              {parentTab?.id === 2 && <Button
                className="underline-animation bg-white text-yellow-600 hover:bg-white font-semibold text-[14px] transition-colors duration-200"
                onClick={() => window.open(`${urls.EXTENSION}${urls.BOM_ANALYSIS}?dateRangeFilter=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&shiftFilter=${active?.id}&yAxis=${selectedTab}`, '_blank')}
              >
                <BarChart3 size={20} className='transition-transform duration-300  hover:scale-110 scale-100' />
                Production Analysis
                <ArrowRight size={18} /></Button>}
              {!shouldHideSort && SortSection}
            </div>
          </div>
          <TableWithTabs
            tableInfo={getTableInfo()}
            renderLegend={renderLegend}
            tabInfo={getTabInfo()}
            height={400}
            highLightRow={getIndex(rowIndex)}
            deferRender={true}
          />
        </>
      )}
    </div>
  );
}

export default AiFmsPage;